import z from 'zod';
import {
  AllCharLabelSchema,
  CognitoUserIdSchema,
  UnixTimestampSchema,
  UUIDSchema,
} from '../common/schemas';
import { DBJsonObjectSchema, DBTimestampSchema, JsonObjectSchema } from '../common/sql';
import { AttemptStatus } from './enums';

const MarksSchema = z.number().int().min(0);
const MarkingNoteSchema = AllCharLabelSchema.min(1).max(2048).nullable();

/**
 * DB Schemas
 */

export const DBAttemptIdSchema = UUIDSchema;
export const DBAttemptNumberSchema = z.number().int().positive();
export const DBAttemptStatusSchema = z.enum(AttemptStatus);

export const DBAttemptSchema = z.object({
  id: DBAttemptIdSchema,
  assignmentId: UUIDSchema,
  userId: CognitoUserIdSchema,
  attemptNumber: DBAttemptNumberSchema,
  status: DBAttemptStatusSchema,
  startedAt: DBTimestampSchema,
  submittedAt: DBTimestampSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBSetAssignmentSchema = z.object({
  id: UUIDSchema,
  setId: UUIDSchema,
  opensAt: DBTimestampSchema.nullable(),
  closesAt: DBTimestampSchema.nullable(),
  timeLimitSeconds: z.number().int().positive().nullable(),
  maxAttempts: z.number().int().positive(),
  settings: DBJsonObjectSchema,
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBAssignmentTargetSchema = z.object({
  assignmentId: UUIDSchema,
  userId: CognitoUserIdSchema,
  createdAt: DBTimestampSchema,
});

export const DBAttemptResponseSchema = z.object({
  id: UUIDSchema,
  attemptId: DBAttemptIdSchema,
  questionId: UUIDSchema,
  responseTypeId: UUIDSchema,
  bodyLatex: z.string().nullable(),
  selectedOptionId: UUIDSchema.nullable(),
  timeSpentSeconds: z.number().int().min(0).nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBMarkingEventSchema = z.object({
  id: UUIDSchema,
  responseId: UUIDSchema,
  eventNumber: z.number().int().positive(),
  methodId: UUIDSchema,
  markerId: CognitoUserIdSchema.nullable(),
  note: MarkingNoteSchema,
  createdAt: DBTimestampSchema,
});

export const DBCriterionMarkSchema = z.object({
  id: UUIDSchema,
  markingEventId: UUIDSchema,
  criterionId: UUIDSchema,
  marksAwarded: MarksSchema,
  comment: MarkingNoteSchema,
  createdAt: DBTimestampSchema,
});

/** A row of `attempt_totals`. */
export const DBAttemptTotalsSchema = z.object({
  attemptId: DBAttemptIdSchema,
  assignmentId: UUIDSchema,
  userId: CognitoUserIdSchema,
  status: DBAttemptStatusSchema,
  responses: z.number().int().min(0),
  responsesMarked: z.number().int().min(0),
  marksAwarded: MarksSchema.nullable(),
  marksAttempted: MarksSchema.nullable(),
  marksAvailable: MarksSchema.nullable(),
});

/**
 * Client Schemas
 */

export const AttemptIdSchema = DBAttemptIdSchema;
export const AttemptNumberSchema = DBAttemptNumberSchema;
export const AttemptStatusSchema = z.enum(AttemptStatus).openapi({
  description: 'in_progress while it is sat, submitted once handed in, then marked',
  example: AttemptStatus.Marked,
});

export const AttemptSchema = z.object({
  id: AttemptIdSchema,
  assignmentId: UUIDSchema,
  userId: CognitoUserIdSchema,
  attemptNumber: AttemptNumberSchema,
  status: AttemptStatusSchema,
  startedAt: UnixTimestampSchema,
  submittedAt: UnixTimestampSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const SetAssignmentSchema = z.object({
  id: UUIDSchema,
  setId: UUIDSchema,
  opensAt: UnixTimestampSchema.nullable(),
  closesAt: UnixTimestampSchema.nullable(),
  timeLimitSeconds: z.number().int().positive().nullable(),
  maxAttempts: z.number().int().positive(),
  settings: JsonObjectSchema,
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const AssignmentTargetSchema = z.object({
  assignmentId: UUIDSchema,
  userId: CognitoUserIdSchema,
  createdAt: UnixTimestampSchema,
});

export const AttemptResponseSchema = z.object({
  id: UUIDSchema,
  attemptId: AttemptIdSchema,
  questionId: UUIDSchema,
  responseTypeId: UUIDSchema,
  bodyLatex: z.string().nullable(),
  selectedOptionId: UUIDSchema.nullable(),
  timeSpentSeconds: z.number().int().min(0).nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const MarkingEventSchema = z.object({
  id: UUIDSchema,
  responseId: UUIDSchema,
  eventNumber: z.number().int().positive(),
  markingMethodId: UUIDSchema,
  markerId: CognitoUserIdSchema.nullable(),
  note: MarkingNoteSchema,
  createdAt: UnixTimestampSchema,
});

export const CriterionMarkSchema = z.object({
  id: UUIDSchema,
  markingEventId: UUIDSchema,
  criterionId: UUIDSchema,
  marksAwarded: MarksSchema,
  comment: MarkingNoteSchema,
  createdAt: UnixTimestampSchema,
});

export const AttemptTotalsSchema = z.object({
  attemptId: AttemptIdSchema,
  assignmentId: UUIDSchema,
  userId: CognitoUserIdSchema,
  status: AttemptStatusSchema,
  responses: z.number().int().min(0),
  responsesMarked: z.number().int().min(0),
  marksAwarded: MarksSchema.nullable().openapi({
    description:
      'The marks awarded so far. Null until a response is marked, and for an attempt with no responses.',
    example: 7,
  }),
  marksAttempted: MarksSchema.nullable(),
  marksAvailable: MarksSchema.nullable().openapi({
    description: 'The marks the whole set is worth',
    example: 10,
  }),
});
